import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DataEditor, GridCellKind, GridColumn, Item, GridCell, CompactSelection } from '@glideapps/glide-data-grid';
import { AlertTriangle, Save, RefreshCw, Filter, Search } from 'lucide-react';
import { apiFetch } from '@/utils/api';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useI18n } from '@/hooks/useI18n';
import CategoryDropdown from '@/components/CategoryDropdown';

interface ProductItem {
    id: string;
    name: string;
    vendor_name?: string | null; // optional for revenue items
    category_id: string | null;
    category_name: string | null;
    status: string;
    created_at: string;
}

interface CategoryOption {
    id: string;
    name: string;
    parent_name: string | null;
}

interface CategoryStats {
    total_products: number;
    uncategorized_products: number;
    parent_category_products: number;
    properly_categorized: number;
    categorization_rate: number;
}

export default function CategoryManagementPage() {
    const { t } = useI18n();
    const [activeTab, setActiveTab] = useState<'cost' | 'revenue'>('cost');
    const isRevenue = activeTab === 'revenue';

    const [products, setProducts] = useState<ProductItem[]>([]);
    const [categories, setCategories] = useState<CategoryOption[]>([]);
    const [stats, setStats] = useState<CategoryStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedRows, setSelectedRows] = useState<CompactSelection>(CompactSelection.empty());

    // Filters
    const [vendorFilter, setVendorFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    // Bulk operations
    const [bulkCategoryId, setBulkCategoryId] = useState<string | null | undefined>(undefined);
    const [isSaving, setIsSaving] = useState(false);

    // Revenue category creation state
    const [newRevCatName, setNewRevCatName] = useState('');
    const [newRevCatParentId, setNewRevCatParentId] = useState<string>('');
    const [creatingRevCat, setCreatingRevCat] = useState(false);

    const uncategorizedLabel = t('categoryManagement.uncategorized', 'admin');
    const unknownLabel = t('categoryManagement.unknown', 'admin');
    const showAddNewCat = false;

    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Build query parameters for filtering (category filter is frontend-only)
            const params = new URLSearchParams();
            // Note: search is now handled by built-in DataEditor search
            if (!isRevenue && vendorFilter && vendorFilter !== 'all') params.append('vendor_filter', vendorFilter);
            if (statusFilter && statusFilter !== 'all') params.append('status_filter', statusFilter);
            params.append('limit', '3000'); // Load more for better UX

            const base = isRevenue ? '/api/category-management/revenue' : '/api/category-management';
            const itemsEndpoint = isRevenue ? `${base}/items?${params.toString()}` : `${base}/products?${params.toString()}`;
            const categoriesEndpoint = isRevenue ? `${base}/categories` : `${base}/categories`;

            const [itemsRes, categoriesRes, statsRes] = await Promise.all([
                apiFetch(itemsEndpoint).then(res => res.json()),
                apiFetch(categoriesEndpoint).then(res => res.json()),
                isRevenue ? Promise.resolve(null) : apiFetch(`${base}/stats`).then(res => res.json()).catch(() => null)
            ]);

            setProducts(Array.isArray(itemsRes) ? itemsRes : []);
            setCategories(Array.isArray(categoriesRes) ? categoriesRes : []);
            setStats(statsRes);
        } catch (err: any) {
            setError(err.message || t('categoryManagement.loadFailed', 'admin'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [activeTab]);

    // Reload data when filters change (but not category filter - that's frontend only)
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (!loading) {
                loadData();
            }
        }, 500); // Debounce filter changes

        return () => clearTimeout(timeoutId);
    }, [vendorFilter, statusFilter, activeTab]); // Reload when tab changes

    // Filter products based on search and filters
    const filteredProducts = useMemo(() => {
        const filtered = products.filter(product => {
            const matchesVendor = isRevenue || vendorFilter === 'all' ||
                (vendorFilter === 'unknown' && !product.vendor_name) ||
                (product.vendor_name && product.vendor_name.toLowerCase().includes(vendorFilter.toLowerCase()));

            const matchesCategory = categoryFilter === 'all' ||
                (categoryFilter === 'uncategorized' && (!product.category_name || product.category_name === 'Uncategorized')) ||
                (categoryFilter === 'parent_categories' && product.category_name && categories.find(cat => cat.name === product.category_name && !cat.parent_name)) ||
                (categoryFilter === 'leaf_categories' && product.category_name && categories.find(cat => cat.name === product.category_name && cat.parent_name)) ||
                (product.category_id === categoryFilter); // Direct category ID match

            const matchesStatus = statusFilter === 'all' || product.status === statusFilter;

            return matchesVendor && matchesCategory && matchesStatus;
        });

        // Debug logging when no results found
        if (filtered.length === 0 && products.length > 0) {
            console.log('No filtered results found:', {
                totalProducts: products.length,
                vendorFilter,
                categoryFilter,
                statusFilter,
                sampleProducts: products.slice(0, 3).map(p => ({
                    name: p.name,
                    vendor: p.vendor_name,
                    category: p.category_name,
                    status: p.status
                }))
            });
        }

        return filtered;
    }, [products, vendorFilter, categoryFilter, statusFilter, categories, isRevenue]);

    // State for search
    const [searchValue, setSearchValue] = useState('');

    // State for column sorting
    const [sortColumn, setSortColumn] = useState<number | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    // Sort and search filtered products
    const sortedProducts = useMemo(() => {
        let result = [...filteredProducts];

        // Apply search filter
        if (searchValue.trim()) {
            const searchLower = searchValue.toLowerCase();
            result = result.filter(product =>
                product.name.toLowerCase().includes(searchLower) ||
                (product.vendor_name && product.vendor_name.toLowerCase().includes(searchLower)) ||
                (product.category_name && product.category_name.toLowerCase().includes(searchLower))
            );
        }

        // Apply sorting if column is selected
        if (sortColumn !== null) {
            result.sort((a, b) => {
                let aValue: string | number;
                let bValue: string | number;

                switch (sortColumn) {
                    case 0: // Product Name
                        aValue = a.name.toLowerCase();
                        bValue = b.name.toLowerCase();
                        break;
                    case 1: // Vendor
                        aValue = (a.vendor_name || '').toLowerCase();
                        bValue = (b.vendor_name || '').toLowerCase();
                        break;
                    case 2: // Category
                        aValue = (a.category_name || '').toLowerCase();
                        bValue = (b.category_name || '').toLowerCase();
                        break;
                    case 3: // Status
                        aValue = a.status.toLowerCase();
                        bValue = b.status.toLowerCase();
                        break;
                    default:
                        return 0;
                }

                if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return result;
    }, [filteredProducts, searchValue, sortColumn, sortDirection]);

    // Handle column header click for sorting
    const onHeaderClicked = useCallback((col: number) => {
        if (sortColumn === col) {
            // Toggle sort direction if clicking the same column
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            // Set new sort column and default to ascending
            setSortColumn(col);
            setSortDirection('asc');
        }
    }, [sortColumn]);

    // Grid columns configuration
    const columns: GridColumn[] = useMemo(() => [
        {
            title: `${t('categoryManagement.columns.productName', 'admin')}${sortColumn === 0 ? (sortDirection === 'asc' ? ' ↑' : ' ↓') : ''}`,
            id: "name",
            width: 500,
            sortable: true,
        },
        {
            title: `${t('categoryManagement.columns.vendor', 'admin')}${sortColumn === 1 ? (sortDirection === 'asc' ? ' ↑' : ' ↓') : ''}`,
            id: "vendor",
            width: 400,
            sortable: true,
        },
        {
            title: `${t('categoryManagement.columns.category', 'admin')}${sortColumn === 2 ? (sortDirection === 'asc' ? ' ↑' : ' ↓') : ''}`,
            id: "category",
            width: 200,
            sortable: true,
        }
    ], [sortColumn, sortDirection, t]);

    // Get cell content for the grid
    const getCellContent = useCallback((cell: Item): GridCell => {
        const [col, row] = cell;
        const product = sortedProducts[row];

        if (!product) {
            return {
                kind: GridCellKind.Text,
                data: "",
                displayData: "",
                allowOverlay: false,
            };
        }

        switch (col) {
            case 0: // Product Name
                return {
                    kind: GridCellKind.Text,
                    data: product.name,
                    displayData: product.name,
                    allowOverlay: false,
                };
            case 1: // Vendor
                return {
                    kind: GridCellKind.Text,
                    data: product.vendor_name || unknownLabel,
                    displayData: product.vendor_name || unknownLabel,
                    allowOverlay: false,
                };
            case 2: // Category
                const categoryDisplay = product.category_name || uncategorizedLabel;

                return {
                    kind: GridCellKind.Text,
                    data: categoryDisplay,
                    displayData: categoryDisplay,
                    allowOverlay: true,
                    readonly: false,
                };
            default:
                return {
                    kind: GridCellKind.Text,
                    data: "",
                    displayData: "",
                    allowOverlay: false,
                };
        }
    }, [sortedProducts, uncategorizedLabel, unknownLabel]);

    // State for inline category dropdown
    const [editingCell, setEditingCell] = useState<{ row: number, col: number, rect: DOMRect } | null>(null);

    // Handle cell click for category editing
    const onCellClicked = useCallback((cell: Item, event: any) => {
        const [col, row] = cell;

        if (col === 2) { // Category column
            const product = sortedProducts[row];
            if (product) {
                // Get the cell position from the event
                const rect = event.bounds || { x: 0, y: 0, width: 250, height: 32 };
                setEditingCell({ row, col, rect });
            }
        }
    }, [sortedProducts]);

    // Handle category selection
    const handleCategorySelect = useCallback(async (categoryId: string | null) => {
        if (!editingCell) return;

        const product = sortedProducts[editingCell.row];
        if (!product) return;

        try {
            const endpoint = isRevenue
                ? `/api/category-management/revenue/item/${product.id}/category`
                : `/api/category-management/product/${product.id}/category`;
            const payload = isRevenue
                ? { item_id: product.id, category_id: categoryId }
                : { product_id: product.id, category_id: categoryId };

            await apiFetch(endpoint, {
                method: 'PUT',
                body: JSON.stringify(payload)
            });

            // Update local state
            const category = categories.find(cat => cat.id === categoryId);
            setProducts(prev => prev.map(p =>
                p.id === product.id
                    ? { ...p, category_id: categoryId, category_name: category?.name || null }
                    : p
            ));

            // Clear editing state
            setEditingCell(null);
            setError(null);

        } catch (err: any) {
            setError(err.message || t('categoryManagement.updateFailed', 'admin'));
        }
    }, [editingCell, sortedProducts, categories, isRevenue, t]);

    // Handle keyboard events for dropdown
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && editingCell) {
                setEditingCell(null);
            }
        };

        if (editingCell) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [editingCell]);



    // Handle bulk category update
    const handleBulkUpdate = async () => {
        if (selectedRows.length === 0 || bulkCategoryId === undefined) return;

        try {
            setIsSaving(true);
            const selectedProductIds: string[] = [];

            // Use CompactSelection.toArray() to get selected indices
            const selectedIndices = selectedRows.toArray();

            // Get product IDs for selected indices
            selectedIndices.forEach(rowIndex => {
                if (sortedProducts[rowIndex]) {
                    selectedProductIds.push(sortedProducts[rowIndex].id);
                }
            });

            const endpoint = isRevenue
                ? '/api/category-management/revenue/items/bulk-category'
                : '/api/category-management/products/bulk-category';
            const payload = isRevenue
                ? { item_ids: selectedProductIds, category_id: bulkCategoryId }
                : { product_ids: selectedProductIds, category_id: bulkCategoryId };

            await apiFetch(endpoint, {
                method: 'PUT',
                body: JSON.stringify(payload)
            });

            // Reload data to reflect changes
            await loadData();
            setSelectedRows(CompactSelection.empty());
            setBulkCategoryId(undefined);

        } catch (err: any) {
            setError(err.message || t('categoryManagement.bulkUpdateFailed', 'admin'));
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="container mx-auto px-4 py-8">
                    <div className="flex items-center justify-center h-64">
                        <RefreshCw className="h-8 w-8 animate-spin" />
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                <div className="mb-6">
                    <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
                        <Filter className="h-8 w-8" />
                        {t('categoryManagement.title', 'admin')}
                    </h1>
                    <p className="text-muted-foreground mt-2">
                        {t('categoryManagement.subtitle', 'admin')}
                    </p>
                </div>

                {error && (
                    <Alert className="mb-6">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {/* Tabs for cost vs revenue categories */}
                <div className="mb-4">
                    <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'cost' | 'revenue')}>
                        <TabsList>
                            <TabsTrigger value="cost">{t('categoryManagement.costTab', 'admin')}</TabsTrigger>
                            <TabsTrigger value="revenue">{t('categoryManagement.revenueTab', 'admin')}</TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>

                {/* Revenue category creation */}
                {isRevenue && showAddNewCat && (
                    <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-2">
                        <Input
                            placeholder={t('categoryManagement.newRevenueCategory', 'admin')}
                            value={newRevCatName}
                            onChange={(e) => setNewRevCatName(e.target.value)}
                        />
                        <Select value={newRevCatParentId} onValueChange={(v) => setNewRevCatParentId(v === '__none__' ? '' : v)}>
                            <SelectTrigger>
                                <SelectValue placeholder={t('categoryManagement.parentOptional', 'admin')} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__none__">{t('categoryManagement.noParent', 'admin')}</SelectItem>
                                {[...categories]
                                    .sort((a, b) => {
                                        const aParent = a.parent_name || a.name;
                                        const bParent = b.parent_name || b.name;
                                        if (aParent !== bParent) return aParent.localeCompare(bParent);
                                        return a.name.localeCompare(b.name);
                                    })
                                    .map((category) => (
                                        <SelectItem key={category.id} value={category.id}>
                                            {category.parent_name ? `${category.parent_name} → ${category.name}` : category.name}
                                        </SelectItem>
                                    ))}
                            </SelectContent>
                        </Select>
                        <Button
                            onClick={async () => {
                                if (!newRevCatName) return;
                                try {
                                    setCreatingRevCat(true);
                                    await apiFetch('/api/category-management/revenue/categories', {
                                        method: 'POST',
                                        body: JSON.stringify({ name: newRevCatName, parent_id: newRevCatParentId || null })
                                    });
                                    setNewRevCatName('');
                                    setNewRevCatParentId('');
                                    await loadData();
                                } catch (err: any) {
                                    setError(err.message || t('categoryManagement.createRevenueFailed', 'admin'));
                                } finally {
                                    setCreatingRevCat(false);
                                }
                            }}
                            disabled={!newRevCatName || creatingRevCat}
                        >
                            {t('categoryManagement.addCategory', 'admin')}
                        </Button>
                    </div>
                )}

                {/* Data Grid */}
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-xl font-semibold">
                                    {isRevenue ? t('categoryManagement.items', 'admin') : t('categoryManagement.products', 'admin')} ({sortedProducts.length})
                                    {categoryFilter !== 'all' && (
                                        <span className="text-sm font-normal text-blue-600 ml-2">
                                            • {t('categoryManagement.filteredBy', 'admin')}: {
                                                categoryFilter === 'uncategorized' ? t('categoryManagement.uncategorized', 'admin') :
                                                    categoryFilter === 'parent_categories' ? t('categoryManagement.parentCategories', 'admin') :
                                                        categoryFilter === 'leaf_categories' ? t('categoryManagement.leafCategories', 'admin') :
                                                            categories.find(cat => cat.id === categoryFilter)?.parent_name
                                                                ? `${categories.find(cat => cat.id === categoryFilter)?.parent_name} → ${categories.find(cat => cat.id === categoryFilter)?.name}`
                                                                : categories.find(cat => cat.id === categoryFilter)?.name || t('categoryManagement.unknownCategory', 'admin')
                                            }
                                        </span>
                                    )}
                                </h2>
                                <p className="text-sm text-muted-foreground mt-1">
                                    {t('categoryManagement.gridHelp', 'admin')}
                                 </p>


                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                            <div>
                                <div className="relative">
                                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder={t('categoryManagement.searchPlaceholder', 'admin')}
                                        value={searchValue}
                                        onChange={(e) => setSearchValue(e.target.value)}
                                        className="pl-10"
                                    />
                                </div>
                            </div>

                            <div>
                                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t('categoryManagement.allCategories', 'admin')}</SelectItem>
                                        <SelectItem value="uncategorized">{t('categoryManagement.uncategorizedOnly', 'admin')}</SelectItem>
                                        <SelectItem value="parent_categories">{t('categoryManagement.parentCategories', 'admin')}</SelectItem>
                                        <SelectItem value="leaf_categories">{t('categoryManagement.leafCategories', 'admin')}</SelectItem>
                                        <div className="border-t border-gray-200 my-1"></div>
                                        {[...categories]
                                            .sort((a, b) => {
                                                // First sort by parent category (root)
                                                const aParent = a.parent_name || a.name;
                                                const bParent = b.parent_name || b.name;

                                                if (aParent !== bParent) {
                                                    return aParent.localeCompare(bParent);
                                                }

                                                // Then sort by leaf category name
                                                return a.name.localeCompare(b.name);
                                            })
                                            .map((category) => (
                                                <SelectItem key={category.id} value={category.id}>
                                                    {category.parent_name ? `${category.parent_name} → ${category.name}` : category.name}
                                                </SelectItem>
                                            ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="flex-1">
                                    <CategoryDropdown
                                        selectedCategoryId={bulkCategoryId}
                                        onCategoryChange={setBulkCategoryId}
                                        placeholder={t('categoryManagement.bulkSelectPlaceholder', 'admin')}
                                        allOptionLabel={t('categoryManagement.uncategorized', 'admin')}
                                        includeAllOption={true}
                                        categoryType={isRevenue ? 'revenue' : 'cost'}
                                    />
                                </div>
                                <Button
                                    onClick={handleBulkUpdate}
                                    disabled={selectedRows.length === 0 || bulkCategoryId === undefined || isSaving}
                                >
                                    <Save className="h-4 w-4 mr-2" />
                                    {
                                    t('categoryManagement.bulkUpdate', 'admin', {
                                        count: selectedRows.length ?? '0',
                                        label: isRevenue ? t('categoryManagement.items', 'admin') : t('categoryManagement.products', 'admin'),
                                    })}
                                </Button>
                            </div>
                        </div>

                        {sortedProducts.length === 0 ? (
                            <div style={{ height: '600px', width: '100%' }} className="flex items-center justify-center">
                                <div className="text-center">
                                    <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                                        {t('categoryManagement.noProductsFound', 'admin')}
                                    </h3>
                                    <p className="text-gray-500 mb-4">
                                        {searchValue || vendorFilter !== 'all' || categoryFilter !== 'all' || statusFilter !== 'all'
                                            ? t('categoryManagement.noProductsHint', 'admin')
                                            : t('categoryManagement.noProductsAvailable', 'admin')
                                        }
                                    </p>
                                    {(searchValue || vendorFilter !== 'all' || categoryFilter !== 'all' || statusFilter !== 'all') && (
                                        <Button
                                            variant="outline"
                                            onClick={() => {
                                                setSearchValue('');
                                                setVendorFilter('all');
                                                setCategoryFilter('all');
                                                setStatusFilter('all');
                                            }}
                                        >
                                            {t('actions.clearAllFilters', 'common')}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div style={{ height: 'calc(100vh - 400px)', minHeight: '600px', width: '100%' }}>
                                <DataEditor
                                    getCellContent={getCellContent}
                                    columns={columns}
                                    rows={sortedProducts.length}
                                    onCellClicked={onCellClicked}
                                    onHeaderClicked={onHeaderClicked}
                                    gridSelection={{
                                        rows: selectedRows,
                                        columns: CompactSelection.empty(),
                                    }}
                                    onGridSelectionChange={(selection) => {
                                        setSelectedRows(selection.rows);
                                    }}
                                    rowMarkers="both"
                                    smoothScrollX={true}
                                    smoothScrollY={true}
                                    keybindings={{
                                        search: true,
                                    }}
                                    getCellsForSelection={true}
                                    theme={{
                                        accentColor: "#3b82f6",
                                        accentLight: "#dbeafe",
                                        textDark: "#1f2937",
                                        textMedium: "#6b7280",
                                        textLight: "#9ca3af",
                                        textBubble: "#ffffff",
                                        bgIconHeader: "#f9fafb",
                                        fgIconHeader: "#6b7280",
                                        textHeader: "#374151",
                                        textHeaderSelected: "#1f2937",
                                        bgCell: "#ffffff",
                                        bgCellMedium: "#f9fafb",
                                        bgHeader: "#f3f4f6",
                                        bgHeaderHasFocus: "#e5e7eb",
                                        bgHeaderHovered: "#f3f4f6",
                                        bgBubble: "#ffffff",
                                        bgBubbleSelected: "#3b82f6",
                                        bgSearchResult: "#fef3c7",
                                        borderColor: "#e5e7eb",
                                        drilldownBorder: "#d1d5db",
                                        linkColor: "#3b82f6",
                                        headerFontStyle: "600 13px",
                                        baseFontStyle: "13px",
                                        fontFamily: "Inter, Roboto, -apple-system, BlinkMacSystemFont, avenir next, avenir, segoe ui, helvetica neue, helvetica, Ubuntu, noto, arial, sans-serif",
                                    }}
                                />
                            </div>
                        )}


                {/* Inline Category Dropdown */}
                {editingCell && (
                    <>
                        {/* Backdrop to close dropdown when clicking outside */}
                        <div
                            className="fixed inset-0 z-40"
                            onClick={() => setEditingCell(null)}
                        />

                        {/* Inline dropdown positioned at cell location */}
                        <div
                            className="fixed z-50 min-w-[320px] max-w-[460px]"
                            style={{
                                left: Math.min(editingCell.rect.x, window.innerWidth - 480), // Prevent going off right edge
                                top: editingCell.rect.y + editingCell.rect.height + 2,
                            }}
                        >
                            <CategoryDropdown
                                selectedCategoryId={sortedProducts[editingCell.row]?.category_id || null}
                                onCategoryChange={(categoryId) => {
                                    handleCategorySelect(categoryId);
                                }}
                                placeholder={t('forms.category', 'common')}
                                allOptionLabel={t('categoryManagement.uncategorized', 'admin')}
                                includeAllOption={true}
                                categoryType={isRevenue ? 'revenue' : 'cost'}
                                defaultOpen={true}
                                hideTrigger={true}
                            />
                        </div>
                    </>
                )}
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/admin/category-management')({
    component: CategoryManagementPage,
})
